import { Database, Cpu, Send, TrendingUp, DollarSign } from 'lucide-react';

/**
 * The five-band founder pulse, in causal order:
 *
 *   data in → engine produces → it goes out → it creates value → it earns
 *
 * The order is the point. A problem in an early band explains every empty
 * number downstream of it, so reading left to right tells you WHERE the chain
 * broke rather than just that something is wrong.
 */

export type Health = 'ok' | 'warn' | 'bad' | 'idle';

export interface Pulse {
  generated_at?: string;
  pipeline?: any;
  engine?: any;
  delivery?: any;
  value?: any;
  revenue?: any;
  users?: any[];
}

const TONE: Record<Health, { dot: string; text: string }> = {
  ok:   { dot: 'bg-[#2E6B4F]', text: 'text-[#2E6B4F]' },
  warn: { dot: 'bg-[#C98A3C]', text: 'text-[#C98A3C]' },
  bad:  { dot: 'bg-[#B54A4A]', text: 'text-[#B54A4A]' },
  idle: { dot: 'bg-[#C4BFC9]', text: 'text-[#6B6670]' },
};

const money = (v: any) => {
  const n = Number(v);
  return '$' + (isFinite(n) ? Math.round(n) : 0).toLocaleString('en-US');
};

const ago = (stamp?: string | null) => {
  if (!stamp) return 'never';
  const then = new Date(stamp).getTime();
  if (!isFinite(then)) return 'never';
  const h = (Date.now() - then) / 3.6e6;
  if (h < 1) return `${Math.max(1, Math.round(h * 60))}m ago`;
  if (h < 48) return `${Math.round(h)}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

function Band({
  icon, label, health, headline, detail, lines,
}: {
  icon: React.ReactNode;
  label: string;
  health: Health;
  headline: string;
  detail?: string;
  lines: Array<{ text: string; tone?: Health }>;
}) {
  const tone = TONE[health];
  return (
    <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-5 shadow-sm flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${tone.dot}`} aria-hidden />
        <span className="font-sans text-[10px] text-[#6B6670] uppercase tracking-[0.12em]">
          {label}
        </span>
        <span className="ml-auto text-[#C4BFC9]" aria-hidden>{icon}</span>
      </div>

      <p className={`font-serif text-[26px] leading-none mb-1 ${tone.text}`}>{headline}</p>
      {detail && <p className="font-sans text-xs text-[#6B6670] mb-3">{detail}</p>}

      <ul className="mt-auto space-y-1 pt-2">
        {lines.map((l, i) => (
          <li
            key={i}
            className={`font-sans text-xs leading-relaxed ${
              l.tone ? TONE[l.tone].text : 'text-[#6B6670]'
            }`}
          >
            {l.text}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function PulseBands({ pulse }: { pulse: Pulse }) {
  const pipe = pulse.pipeline || {};
  const eng = pulse.engine || {};
  const del = pulse.delivery || {};
  const val = pulse.value || {};
  const rev = pulse.revenue || {};

  const providers: any[] = pipe.providers || [];
  const connections = pipe.connections_total || 0;
  const stale = pipe.stale_total || 0;

  // ---- ① PIPELINE ---------------------------------------------------------
  // Configuration leads the band. An integration with no credentials and one
  // that is simply idle used to render identically — both just empty — and
  // that ambiguity is what made a missing SQUARE_APP_SECRET look like a
  // broken product. Nothing downstream can happen until this line is green.
  const integrations: any[] = pipe.integrations || [];
  const anyConfigured = integrations.some((i) => i.configured);

  const configLines = integrations.map((i) =>
    !i.configured
      ? {
          text: `${i.provider}: not configured — missing ${(i.missing || []).join(' + ')}`,
          tone: 'bad' as Health,
        }
      : {
          text: `${i.provider}: configured · ${i.environment}${
            i.sandbox_warning ? ' — TEST DATA ONLY' : ''
          }`,
          tone: (i.sandbox_warning ? 'warn' : 'ok') as Health,
        }
  );

  // Weather runs on Open-Meteo's free, non-commercial tier until a key is set;
  // once anyone pays, the free tier is no longer allowed (admin_metrics.weather_config).
  const weather = pipe.weather;
  const weatherLines = !weather
    ? []
    : [{
        text: weather.warning
          ? `${weather.label} — ${weather.paying_users} paying; set OPEN_METEO_API_KEY`
          : weather.label,
        tone: (weather.warning ? 'bad' : weather.plan === 'paid' ? 'ok' : undefined) as Health,
      }];

  const pipeHealth: Health =
    !anyConfigured ? 'bad'
      : connections === 0 ? 'idle'
      : stale > 0 ? 'bad'
      : pipe.transactions_7d === 0 ? 'warn'
      : 'ok';
  const pipeLines = !anyConfigured
    ? []
    : connections === 0
    ? [{ text: 'Configured, but no merchant has connected yet.' }]
    : providers.map((p) => ({
        text: `${p.provider}: ${p.connections} connected · synced ${ago(p.last_synced_at)}${
          p.stale ? ` · ${p.stale} stale` : ''
        }`,
        tone: (p.stale ? 'bad' : 'ok') as Health,
      }));

  // ---- ② ENGINE -----------------------------------------------------------
  const ticks = eng.ticks_24h ?? 0;
  const expected = eng.ticks_24h_expected ?? 24;
  const registered = eng.detectors_registered ?? 0;
  const fired = eng.detectors_ever_fired ?? 0;
  const engHealth: Health =
    ticks === 0 ? 'bad' : ticks < expected * 0.8 ? 'warn' : 'ok';

  // ---- ③ DELIVERY ---------------------------------------------------------
  const delHealth: Health =
    (del.failed_7d || 0) > 0 ? 'bad'
      : (del.suppressed_7d || 0) > 0 ? 'warn'
      : (del.composed_7d || 0) === 0 ? 'idle'
      : 'ok';

  // ---- ④ VALUE ------------------------------------------------------------
  const measured = val.measured || 0;
  const flopRate = val.didnt_pan_out_rate || 0;
  const valHealth: Health =
    measured === 0 ? 'idle' : flopRate > 0.5 ? 'bad' : flopRate > 0.3 ? 'warn' : 'ok';

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 mb-8">
      <Band
        icon={<Database size={15} />}
        label="1 · Data in"
        health={pipeHealth}
        headline={!anyConfigured ? 'Not set up' : connections === 0 ? 'None' : `${connections}`}
        detail={
          !anyConfigured
            ? 'no POS credentials'
            : connections === 0
            ? 'no connections'
            : `connection${connections === 1 ? '' : 's'}`
        }
        lines={[
          ...configLines,
          ...weatherLines,
          ...pipeLines,
          { text: `${(pipe.transactions_7d || 0).toLocaleString()} transactions in 7d`,
            tone: (pipe.transactions_7d ? undefined : 'warn') as Health },
          { text: `${pipe.uploads_7d || 0} uploads in 7d` },
        ]}
      />

      <Band
        icon={<Cpu size={15} />}
        label="2 · Engine"
        health={engHealth}
        headline={`${ticks}/${expected}`}
        detail="hourly ticks in 24h"
        lines={[
          { text: `Last run ${ago(eng.last_run?.started_at)} · ${eng.last_run?.status || 'never run'}`,
            tone: (eng.last_run?.status === 'failed' ? 'bad' : undefined) as Health },
          // The number worth staring at: how much of the engine is alive.
          { text: `${fired} of ${registered} detectors have ever fired`,
            tone: (registered && fired < registered / 2 ? 'warn' : undefined) as Health },
          { text: `${eng.detectors_fired_30d || 0} fired in the last 30d` },
        ]}
      />

      <Band
        icon={<Send size={15} />}
        label="3 · Delivered"
        health={delHealth}
        headline={`${del.sent_7d || 0}`}
        detail="briefs emailed in 7d"
        lines={[
          { text: `${del.composed_7d || 0} composed in 7d` },
          { text: `${del.failed_7d || 0} send failures`,
            tone: ((del.failed_7d || 0) > 0 ? 'bad' : undefined) as Health },
          // Suppression is not failure, but it looks identical to health if
          // you only watch the failure count.
          { text: `${del.suppressed_7d || 0} suppressed by allowlist`,
            tone: ((del.suppressed_7d || 0) > 0 ? 'warn' : undefined) as Health },
        ]}
      />

      <Band
        icon={<TrendingUp size={15} />}
        label="4 · Value"
        health={valHealth}
        headline={money(val.credited_total)}
        detail="credited to customers"
        lines={[
          { text: `${measured} outcome${measured === 1 ? '' : 's'} measured` },
          { text: `${val.didnt_pan_out || 0} didn't pan out${
              measured ? ` (${Math.round(flopRate * 100)}%)` : ''
            }`,
            tone: (flopRate > 0.3 ? 'warn' : undefined) as Health },
          { text: `${money(val.lifetime_found_total)} found lifetime` },
        ]}
      />

      <Band
        icon={<DollarSign size={15} />}
        label="5 · Revenue"
        health="idle"
        headline={money(rev.estimated_mrr)}
        detail="estimated MRR"
        lines={[
          { text: `${rev.pro_users || 0} paying · ${rev.free_users || 0} free` },
          { text: `${rev.cancelled_users || 0} cancelled` },
          {
            text: (rev.pro_users || 0) === 0
              ? 'Not selling yet — by design.'
              : `${rev.total_users || 0} accounts total`,
          },
        ]}
      />
    </div>
  );
}
